import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryState } from 'nuqs';
import { ArrowLeft, CircleAlert } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Heading,
  PageLoader,
  Paragraph,
} from '@/components/atoms';
import { PageHeader } from '@/components/molecules';
import { useSubscribe, useSubscriptionPlans } from '@/api';
import { routes } from '@/lib/constants';
import { formatCurrency } from '@/lib/helpers/format-currency';
import { getPlanDisplayName } from '@/lib/helpers/subscription-plan';
import type { BillingInterval } from '@/types';

const formatIntervalLabel = (interval: BillingInterval) => {
  switch (interval) {
    case 'monthly':
      return 'month';
    case 'quarterly':
      return 'quarter';
    case 'yearly':
      return 'year';
    default:
      return 'month';
  }
};

export const SubscriptionSummaryPage = () => {
  const navigate = useNavigate();
  const [planIdParam] = useQueryState('planId');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPaystackDetails, setShowPaystackDetails] = useState(false);
  const [isPaystackInitializing, setIsPaystackInitializing] = useState(false);

  const planId = planIdParam ? Number(planIdParam) : null;
  const { data: plans, isLoading } = useSubscriptionPlans();

  const selectedPlan = useMemo(
    () => plans?.find((plan) => plan.id === planId) ?? null,
    [planId, plans]
  );

  const { mutate: subscribe, isPending: isSubscribing } = useSubscribe(
    (data) => {
      if (data.data.authorization_url) {
        window.location.href = data.data.authorization_url;
        return;
      }

      setIsPaystackInitializing(false);
    }
  );

  useEffect(() => {
    if (!planIdParam) {
      navigate(`${routes.settings}?tab=subscription`, { replace: true });
      return;
    }

    if (isLoading) return;

    if (
      !selectedPlan ||
      selectedPlan.package === 'freemium' ||
      selectedPlan.package === 'starter'
    ) {
      navigate(`${routes.settings}?tab=subscription`, { replace: true });
    }
  }, [isLoading, navigate, planIdParam, selectedPlan]);

  if (isLoading || !selectedPlan) {
    return (
      <PageLoader message="Loading subscription summary..." className="py-20" />
    );
  }

  const amount = parseFloat(selectedPlan.price);
  const planName = getPlanDisplayName(selectedPlan);
  const intervalLabel = formatIntervalLabel(selectedPlan.billingInterval);

  const isSubscribeLoading = isSubscribing || isPaystackInitializing;

  const handleSubscribe = () => {
    if (!acceptedTerms || isSubscribeLoading) return;

    setShowPaystackDetails(true);
    setIsPaystackInitializing(true);
    subscribe(
      { planId: selectedPlan.id },
      {
        onError: () => {
          setIsPaystackInitializing(false);
        },
      }
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        heading="Account & Billing / Summary"
        description="Review your subscription before checkout"
      />

      <Button
        variant="link"
        className="w-fit px-0"
        onClick={() => navigate(`${routes.settings}?tab=subscription`)}
      >
        <ArrowLeft />
        Back to plans
      </Button>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card>
          <CardContent className="space-y-6 py-6">
            <div className="space-y-2">
              <Paragraph className="text-sm text-muted-foreground">
                Summary
              </Paragraph>
              <Heading size="lg">
                Subscribe to Sellyt {planName} Subscription
              </Heading>
              <Paragraph className="text-3xl font-semibold">
                {formatCurrency(amount)}{' '}
                <span className="text-base font-normal text-muted-foreground">
                  per {intervalLabel}
                </span>
              </Paragraph>
            </div>

            <div className="space-y-3 border-t border-border pt-4 text-sm">
              <div className="flex items-center justify-between">
                <span>Sellyt {planName}</span>
                <span>{formatCurrency(amount)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span>Subtotal</span>
                <span>{formatCurrency(amount)}</span>
              </div>
              <div className="flex items-center justify-between text-muted-foreground">
                <span>Tax</span>
                <span>{formatCurrency(0)}</span>
              </div>
              <div className="flex items-center justify-between border-t border-border pt-3 text-base font-semibold">
                <span>Total due today</span>
                <span>{formatCurrency(amount)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="space-y-5 py-6">
            {showPaystackDetails ? (
              <>
                <div className="space-y-2">
                  <Heading size="lg">Paystack Gateway</Heading>
                  <Paragraph className="text-sm text-muted-foreground">
                    Debit card, credit card, bank transfer, and USSD payments
                    are handled securely through Paystack.
                  </Paragraph>
                </div>

                <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
                  Make payment using your debit and credit cards. You can also
                  transfer.
                  <Paragraph className="mt-3 flex items-start gap-2 font-semibold text-destructive">
                    <CircleAlert className="mt-0.5 size-4 shrink-0" />
                    Please note that Paystack service charge will apply.
                  </Paragraph>
                </div>

                <Paragraph className="text-xs text-muted-foreground">
                  Your personal data will be used to process your order, support
                  your experience throughout this website, and for other
                  purposes described in our privacy policy.
                </Paragraph>
              </>
            ) : (
              <Paragraph className="text-sm text-muted-foreground">
                Review your plan summary on the left. When you are ready, accept
                the terms below and continue to Paystack checkout.
              </Paragraph>
            )}

            <label className="flex items-start gap-3 text-sm">
              <Checkbox
                checked={acceptedTerms}
                onCheckedChange={(checked) =>
                  setAcceptedTerms(checked === true)
                }
              />
              <span>
                I have read and agree to the website{' '}
                <a
                  href="https://gosellyt.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-primary underline-offset-4 hover:underline"
                >
                  terms and conditions
                </a>
              </span>
            </label>

            <Button
              className="w-full"
              onClick={handleSubscribe}
              isLoading={isSubscribeLoading}
              disabled={!acceptedTerms || isSubscribeLoading}
            >
              {isSubscribeLoading ? 'Initializing Paystack...' : 'Subscribe'}
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
